"use client";

import { useCallback } from "react";

import { checkIsTauri } from "@/shared/lib/tauri";

/**
 * Открывает URL в **дефолтном системном браузере** (а не внутри Tauri webview).
 *
 * Tauri WebView2 на Windows молча блокирует `<a target="_blank">` и `window.open(url)`
 * для https-ссылок — пользователь видит «ничего не происходит». Чтобы открыть
 * внешний браузер из десктоп-приложения, нужно вызвать `shell.open(url)` через
 * Tauri plugin-shell (Rust-сторона уже разрешена через capability `shell:allow-open`).
 *
 * Возвращает onClick-handler:
 *  - В Tauri: preventDefault → invoke shell open
 *  - На сайте: ничего не делает, `<a target="_blank">` отрабатывает штатно
 *
 * Использование:
 * ```tsx
 * const openExternal = useOpenExternal();
 * <a href={url} target="_blank" onClick={openExternal(url)}>OP.GG</a>
 * ```
 */
export function useOpenExternal() {
  return useCallback(
    (url: string) =>
      async (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        if (!checkIsTauri()) return; // обычный браузер — `target=_blank` сработает сам
        e.preventDefault();
        try {
          const { invoke } = await import("@tauri-apps/api/core");
          await invoke("plugin:shell|open", { path: url });
        } catch (err) {
          console.warn("[useOpenExternal] failed to open via shell:", err);
        }
      },
    [],
  );
}
